import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import axios from '../../../../config/axios'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import editPen from '../../../../assets/edit-pen.svg'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import CertificatesContext from '../../../../contexts/CertificatesContext'
import SectionContext from '../../../../contexts/SectionContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ApiService from '../../../../services/ApiService'
import { useTenant } from '../../../../contexts/TenantContext'
import {
  DivInTd,
  GlobalTable,
  HeaderLine,
  Td,
  TdButtons,
  Th,
  Tr,
  FooterTr,
} from '../../../../components/styles/table-style'
import { ConfigMenuButton, ConfigMenuWrapper } from '../../SteelOrders/SteelOrdersTable/style'
import { TableContainer } from '../style'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { StockOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { Settings } from 'lucide-react'
import FieldsManager from '../../../../components/FieldManager'
import { formatReceivedDate, formatStockNumericValue } from './utils'
import { useTooltip } from '../../../../components/Tooltip/useTooltip'

const CellContainer = styled.div`
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  width: 100%;
`

interface OverflowCellProps {
  text: string;
  handleMouseEnter: (e: React.MouseEvent<any>, text: string) => void;
  handleMouseLeave: () => void;
  handleCellClick: (e: React.MouseEvent<any>, text: string) => void;
  forceTooltip?: boolean;
  children?: React.ReactNode;
}

const OverflowCell: React.FC<OverflowCellProps> = ({
  text,
  handleMouseEnter,
  handleMouseLeave,
  handleCellClick,
  forceTooltip = false,
  children
}) => {
  const cellRef = React.useRef<HTMLDivElement>(null);

  const onEnter = (e: React.MouseEvent<any>) => {
    const el = cellRef.current;
    if (el) {
      const isOverflowing = el.scrollWidth > el.clientWidth || forceTooltip;
      if (isOverflowing) {
        handleMouseEnter(e, text);
      }
    }
  };

  const onClick = (e: React.MouseEvent<any>) => {
    const el = cellRef.current;
    if (el) {
      const isOverflowing = el.scrollWidth > el.clientWidth || forceTooltip;
      if (isOverflowing) {
        handleCellClick(e, text);
      }
    }
  };

  return (
    <CellContainer ref={cellRef} onMouseEnter={onEnter} onMouseLeave={handleMouseLeave} onClick={onClick}>
      {children || text}
    </CellContainer>
  );
};

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => {
    const hex = props.$hex;
    if (!hex) return '#bdbdbd';
    return hex.startsWith('#') ? hex : `#${hex}`;
  }};
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-block;
  flex-shrink: 0;
`
const numericStockFields = new Set([
  'dimension1',
  'dimension2',
  'density',
  'availableBalance',
  'totalCmc',
  'unitCmc',
  'lengthInMm',
])

interface Stock {
  id: number
  codeId: number
  material: string
  certificateId: number
  certificateParam?: string
  invoiceId?: number
  invoiceParam?: string
  externalCertificateUrl?: string
  externalInvoiceUrl?: string
  format: string
  finishing: string
  fabrication: string
  heatTreatment: string
  ncm: string
  originCountry: string
  supplier: string
  stockStreet: string
  shelf: string
  stockLocation: string
  quantity: string
  totalWeight: string
  dimension1: string
  dimension2: string
  lengthInMm: string
  costPerKg: string
  costPerMm: string
  code: string
  invoice: any[]
  totalCost: string
  density: string
  certificates: any[]
  availableBalance: string
  measurementUnit: string
  totalCmc: string
  unitCmc: string
  trackingNumber: string
  family: string
  description: string
  stockStatus: string
  characteristic: string
  itemColorId: number | null
  color: string
  pantoneCode: string
  pantoneHex: string
  receivedDate: string
  suppliers?: any[]
  purchaseOrder: string
  nf: string
  certificateNumber: string
  heatNumber: any
  createdAt: string
  updatedAt: string
  movimentType: string
  observation: string
  unitOfMeasurement: string
  isActivated: boolean
  certificateUrl: string | undefined
  prices?: any[]
  quantities?: any[]
}

const StockTable: React.FC = () => {
  const [permissions, setPermissions] = useState<string[]>([])
  const {
    stockQuantity,
    details,
    setStock,
    stockList,
    updateStockList,
    refresh,
    hasMoreStock,
    isLoading,
  } = useContext(StockContext)
  const { stockFilters, setStockFilters } = useContext(TableFiltersContext)
  const { setCertificate } = useContext(CertificatesContext)
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setModalActive, setModalSection, setItIsUpdate, stockMode, setStockMode } =
    useContext(ModalContext)
  const { isFashionTenant } = useTenant()
  const isFashion = isFashionTenant || stockMode === 'fashion'
  const fashionHiddenFields = ['heatTreatment', 'density']
  const { user } = useContext(UserContext)
  const { setTabIndex, fields, setFields, isdropdownOpen, setIsDropdownOpen, setQuantity, quantity } =
    useOutletContext<StockOutletType>()
  const { setStockSection } = useContext(SectionContext)
  const navigate = useNavigate()

  const columnMenuRef = React.useRef<HTMLDivElement>(null)

  const cellRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({})
  const { tooltipElement, handleMouseEnter, handleMouseLeave, handleCellClick } = useTooltip({
    maxWidth: 500,
    maxHeight: 280,
    rowHeight: 22,
    padding: 16,
    verticalGap: 4,
  })

  const getFirstWithEllipsis = (content: string) => {
    if (!content) return '-'
    const parts = content
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return '-'
    if (parts.length === 1) return parts[0]
    return `${parts[0]}...`
  }

  const getQuantityUnitName = (qty: any) =>
    (qty.UnitOfMeasurement?.name || qty.unitOfMeasurement?.name || 'N/A').toUpperCase()

  const formatQuantityEntry = (qty: any) =>
    `${formatStockNumericValue(qty.quantity)} ${getQuantityUnitName(qty)}`

  const getQuantityCellDisplay = (quantities?: any[]) => {
    if (!quantities || quantities.length === 0) return 'N/A'
    const preferred =
      quantities.find((qty) => getQuantityUnitName(qty) === 'MM') ?? quantities[0]
    const formatted = formatQuantityEntry(preferred)
    return quantities.length > 1 ? `${formatted}...` : formatted
  }

  const formatQuantities = (quantities?: any[]) => {
    if (!quantities || quantities.length === 0) return 'N/A'
    return quantities.map(formatQuantityEntry).join('\n')
  }

  const formatPrices = (prices?: any[]) => {
    if (!prices || prices.length === 0) return 'N/A'
    return prices.map(price => {
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(price.price))
      return `${formattedPrice}/${price.UnitOfMeasurement?.name || price.unitOfMeasurement?.name || 'N/A'}`
    }).join('\n')
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setIsDropdownOpen])

  useEffect(() => {
    setStockSection('stock')
  }, [setStockSection])

  const [totalStockCount, setTotalStockCount] = useState<number | undefined>(
    undefined,
  )

  useEffect(() => {
    let mounted = true
    const fetchTotal = async () => {
      try {
        const resp = await ApiService.stock.getTotalStock()
        if (mounted && resp && resp.data != null) {
          const value =
            typeof resp.data === 'number'
              ? resp.data
              : resp.data.totalItems ?? resp.data.total ?? undefined
          setTotalStockCount(value)
        }
      } catch (e) {
        console.error(e)
      }
    }

    fetchTotal()

    return () => {
      mounted = false
    }
  }, [stockFilters, search, refresh])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateStockList(quantity, stockFilters, search)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [quantity, stockFilters, search, refresh])

  useEffect(() => {
    const fetchPermissions = async () => {
      const perms = await getPermissions()
      setPermissions(perms)
    }

    if (user.profileId) {
      fetchPermissions()
    }
  }, [user.profileId])

  const handleCheckCertificate = async (certificateId: number) => {
    const certificateUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates`
    axios
      .get(`${certificateUrl}/certificate/${certificateId}`)
      .then((response) => {
        setModalActive(true)
        setModalSection('viewCertificate')
        setCertificate(response.data)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const filterStockData = (stock: Stock): Partial<Stock> => {
    const allowedFields = [
      'id',
      'codeId',
      'codeId',
      'code',
      'material',
      'format',
      'finishing',
      'fabrication',
      'heatTreatment',
      'originCountry',
      'supplier',
      'suppliers',
      'stockStreet',
      'shelf',
      'stockLocation',
      'ncm',
      'quantity',
      'totalWeight',
      'dimension1',
      'dimension2',
      'lengthInMm',
      'costPerKg',
      'costPerMm',
      'totalCost',
      'invoice',
      'density',
      'certificates',
      'certificateId',
      'certificateParam',
      'invoiceId',
      'invoiceParam',
      'externalCertificateUrl',
      'externalInvoiceUrl',
      'availableBalance',
      'totalCmc',
      'trackingNumber',
      'receivedDate',
      'purchaseOrder',
      'nf',
      'certificateNumber',
      'heatNumber',
      'heatNumber',
      'family',
      'description',
      'stockStatus',
      'characteristic',
      'itemColorId',
      'color',
      'pantoneCode',
      'pantoneHex',
      'measurementUnit',
      'certificateUrl',
      'isActivated',
      'prices',
      'quantities',
    ] as const

    const filtered: any = {}
    allowedFields.forEach((key) => {
      if (key in stock) {
        filtered[key] = stock[key] ?? ''
      }
    })
    return filtered
  }

  const convertSupplierToString = (item: any) => {
    const newItem = { ...item }
    if (typeof item.supplier === 'object' && item.supplier !== null) {
      newItem.supplier = item.supplier?.name || ''
    }
    return newItem
  }

  const handleUpdateClick = (item: Stock) => {
    const filteredItem = filterStockData(item)
    if (filteredItem.receivedDate) {
      filteredItem.receivedDate = formatReceivedDate(filteredItem.receivedDate)
    }
    const convertedSupplier = convertSupplierToString(filteredItem)
    setStock(convertedSupplier)
    setItIsUpdate(true)
    if (isFashionTenant) setStockMode('fashion')
    setModalActive(true)
    setModalSection('firstCustomAdd')
  }

  const handleEntryOrExit = (item: Stock) => {
    const newItem = { ...item }
    if (newItem.receivedDate) {
      newItem.receivedDate = formatReceivedDate(newItem.receivedDate)
    }
    setStock({
      ...newItem,
      itemColorId: newItem.itemColorId ?? null,
      color: newItem.color ?? '',
      pantoneCode: newItem.pantoneCode ?? '',
      pantoneHex: newItem.pantoneHex ?? '',
    })
    setModalActive(true)
    setModalSection('modalEntryOrExit')
  }

  const handleNewModal = (item: any) => {
    const newItem = { ...item }
    if (newItem.receivedDate) {
      newItem.receivedDate = formatReceivedDate(newItem.receivedDate)
    }
    setStock({
      ...newItem,
      itemColorId: newItem.itemColorId ?? null,
      color: newItem.color ?? '',
      pantoneCode: newItem.pantoneCode ?? '',
      pantoneHex: newItem.pantoneHex ?? '',
    })
    if (isFashionTenant) setStockMode('fashion')
    setModalActive(true)
    setModalSection('newModal')
  }

  const getPermissions = async () => {
    try {
      const response = await axios(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/profile/find/${user.profileId}`,
      )

      if (response.data) {
        return response.data.permissions
      }
    } catch (error: any) {
      console.log('Usuário sem perfil')
    }
  }

  const TableComponent: React.FC = () => {
    return (
      <GlobalTable className='short'>
        <thead>
          <HeaderLine>
            {fields.whichFieldsToShow
              .filter((field) => field.checked)
              .filter((field) => !isFashion || !fashionHiddenFields.includes(field.field))
              .filter((field) => isFashion || field.field !== 'color')
              .map((field, index) => (
                <Th key={index}>{field.fieldName}</Th>
              ))}
            <Th>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'inline-block' }}>Ações</div>
                <ConfigMenuWrapper ref={columnMenuRef}>
                  <ConfigMenuButton onClick={() => setIsDropdownOpen((prev) => !prev)} title='Mostrar/ocultar colunas' style={{ color: '#bdbdbd' }}>
                    <Settings size={20} />
                  </ConfigMenuButton>
                  {isdropdownOpen && (
                    <FieldsManager
                      dropdownOpen={setIsDropdownOpen}
                      fields={{
                        ...fields,
                        whichFieldsToShow: fields.whichFieldsToShow
                          .filter(f => !isFashion ? f.field !== 'color' : true)
                          .filter(f => isFashion ? !fashionHiddenFields.includes(f.field) : true)
                      }}
                      setFields={setFields}
                    />
                  )}
                </ConfigMenuWrapper>
              </div>
            </Th>
          </HeaderLine>
        </thead>
        <tbody>
          {stockList.map((item, itemIndex) => {
            const rowClassName = [itemIndex === 0 ? 'first' : '', item.isActivated === false ? 'yellowLine' : '']
              .filter(Boolean)
              .join(' ')

            return (
              <Tr
                key={item.code}
                className={rowClassName}
                style={{ color: item.isActivated === false ? '#9d7d07' : '#000' }}
              >
                {fields.whichFieldsToShow
                  .filter((field) => field.checked)
                  .filter((field) => !isFashion || !fashionHiddenFields.includes(field.field))
                  .filter((field) => isFashion || field.field !== 'color')
                  .map((fieldObj, index) => {
                    const cellValue = item[fieldObj.field as keyof typeof item]

                    return (
                      <Td key={index}>
                        {fieldObj.field === 'certificateUrl' ||
                        fieldObj.field === 'certificates' ||
                        fieldObj.field === 'certificateNumber' ? (
                          (() => {
                            if (item.externalCertificateUrl) {
                              return (
                                <a
                                  href={item.externalCertificateUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  style={{ textDecoration: 'none', textAlign: 'center', color: '#0090d3', fontWeight: 'bold' }}
                                >
                                  {item.certificateNumber || item.certificateParam || 'Visualizar Certificado'}
                                </a>
                              )
                            }
                            if (item.certificateId) {
                              return (
                                <button
                                  onClick={() => handleCheckCertificate(Number(item.certificateId))}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#0090d3',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    padding: 0,
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit'
                                  }}
                                >
                                  {item.certificateNumber || item.certificateParam || `Certificado #${item.certificateId}`}
                                </button>
                              )
                            }
                            const certWithFile = item.HeatNumber?.Certificate?.find((certificate: any) => certificate.supplierFile != null && certificate.supplierFile !== '')
                            if (certWithFile) {
                              return (
                                <a
                                  href={certWithFile.supplierFile}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  style={{ textDecoration: 'none', textAlign: 'center', color: '#0090d3', fontWeight: 'bold' }}
                                >
                                  {item.certificateNumber || item.certificateParam || 'Visualizar'}
                                </a>
                              )
                            }
                            return <span>{item.certificateNumber || item.certificateParam || '-'}</span>
                          })()
                        ) : fieldObj.field === 'invoice' || fieldObj.field === 'nf' ? (
                          (() => {
                            if (item.externalInvoiceUrl) {
                              return (
                                <a
                                  href={item.externalInvoiceUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  style={{ textDecoration: 'none', textAlign: 'center', color: '#0090d3', fontWeight: 'bold' }}
                                >
                                  {item.nf || item.invoiceParam || 'Visualizar NF'}
                                </a>
                              )
                            }
                            if (item.invoiceId) {
                              return (
                                <button
                                  onClick={() => navigate(`/panel/invoice/view/${item.invoiceId}`)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#0090d3',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    padding: 0,
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit'
                                  }}
                                >
                                  {item.nf || item.invoiceParam || `NF #${item.invoiceId}`}
                                </button>
                              )
                            }
                            return <span>{item.nf || item.invoiceParam || '-'}</span>
                          })()
                        ) : fieldObj.field === 'isActivated' ? (
                          <span>{cellValue === true || cellValue === 'true' || cellValue === 1 ? 'Ativo' : 'Inativo'}</span>
                        ) : fieldObj.field === 'quantities' ? (
                          <OverflowCell
                            text={formatQuantities(item.quantities)}
                            handleMouseEnter={handleMouseEnter}
                            handleMouseLeave={handleMouseLeave}
                            handleCellClick={handleCellClick}
                            forceTooltip={item.quantities && item.quantities.length > 1}
                          >
                            {getQuantityCellDisplay(item.quantities)}
                          </OverflowCell>
                        ) : fieldObj.field === 'prices' ? (
                          <OverflowCell
                            text={formatPrices(item.prices)}
                            handleMouseEnter={handleMouseEnter}
                            handleMouseLeave={handleMouseLeave}
                            handleCellClick={handleCellClick}
                            forceTooltip={item.prices && item.prices.length > 1}
                          >
                            {getFirstWithEllipsis(formatPrices(item.prices))}
                          </OverflowCell>
                        ) : ['code', 'material', 'description', 'characteristic'].includes(fieldObj.field) ? (
                          (() => {
                            const raw = (cellValue as any)?.name ?? cellValue
                            if (!raw) return <span>-</span>
                            const textStr = String(raw)
                            return (
                              <OverflowCell
                                text={textStr}
                                handleMouseEnter={handleMouseEnter}
                                handleMouseLeave={handleMouseLeave}
                                handleCellClick={handleCellClick}
                              />
                            )
                          })()
                        ) : fieldObj.field === 'receivedDate' ? (
                          formatReceivedDate(cellValue as string)
                        ) : fieldObj.field === 'color' ? (
                          (() => {
                            const raw = `${cellValue as string}${item.pantoneCode ? ` (${item.pantoneCode})` : ''}`
                            return (
                              <OverflowCell
                                text={raw}
                                handleMouseEnter={handleMouseEnter}
                                handleMouseLeave={handleMouseLeave}
                                handleCellClick={handleCellClick}
                              >
                                <ColorSwatchContainer style={{ cursor: 'inherit' }}>
                                  <ColorSwatchCircle $hex={item.pantoneHex} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {raw}
                                  </span>
                                </ColorSwatchContainer>
                              </OverflowCell>
                            )
                          })()
                        ) : (
                          (() => {
                            const raw = (cellValue as any)?.name ?? cellValue
                            return numericStockFields.has(fieldObj.field)
                              ? formatStockNumericValue(raw)
                              : raw
                          })()
                        )}
                      </Td>
                    )
                  })}
                <TdButtons>
                  <img
                    src={lookup}
                    alt='look up icon'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleNewModal(item)}
                  />
                </TdButtons>
              </Tr>
            )
          })}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td
              colSpan={
                fields.whichFieldsToShow
                  .filter((f) => f.checked)
                  .filter((f) => !isFashion || !fashionHiddenFields.includes(f.field))
                  .length + 1
              }
              style={{
                textAlign: 'center',
                background: 'transparent',
                padding: '1rem 0',
              }}
            >
              <LoadMoreButton
                onLoadMore={() => setQuantity((prev) => prev + 50)}
                currentCount={stockList ? stockList.length : 0}
                totalCount={
                  typeof totalStockCount === 'number'
                    ? totalStockCount
                    : undefined
                }
                hasMore={hasMoreStock}
                isLoading={isLoading}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>
    )
  }

  let tableClass = ''
  if (stockFilters.length !== 0) {
    if (details) {
      tableClass = 'filteredTable'
    } else {
      tableClass = 'filteredTable shortTable'
    }
  } else {
    if (!details) {
      tableClass = 'shortTable'
    }
  }

  return (
    <TableContainer>
      <TableComponent />
      {tooltipElement}
    </TableContainer>
  )
}

export default StockTable
